import { getClientEnv } from './runtime-env';
import oemProfiles from './oems';
import runtimeConfig from './runtime-config.json';

// Environment name translation table
const ENVIRONMENT_ALIASES = {
  local: 'local',
  dev: 'development',
  development: 'development',
  prod: 'production',
  production: 'production',
};

const trimTrailingSlash = (value = '') => value.replace(/\/+$/, '');

const normalizeEnvironment = (value) => {
  if (!value) return null;
  const normalized = String(value).trim().toLowerCase();
  return ENVIRONMENT_ALIASES[normalized] || null;
};

// Ensures that config values are immutable
const deepFreeze = (value) => {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  Object.values(value).forEach(deepFreeze);
  return Object.freeze(value);
};

const requiredString = (value, path, errors) => {
  if (typeof value !== 'string' || !value.trim()) errors.push(`${path} must be a non-empty string`);
};

const validateProfile = (profile) => {
  const errors = [];

  // Check OEM
  requiredString(profile?.id, 'id', errors);
  requiredString(profile?.displayName, 'displayName', errors);
  requiredString(profile?.brand?.icon?.favicon, 'brand.icon.favicon', errors);
  requiredString(profile?.brand?.icon?.appleTouchIcon, 'brand.icon.appleTouchIcon', errors);

  // Check logo - viewBox and padding are optional
  requiredString(profile?.brand?.logo?.href, 'brand.logo.href', errors);
  requiredString(profile?.brand?.logo?.alt, 'brand.logo.alt', errors);
  requiredString(profile?.brand?.logo?.light, 'brand.logo.light', errors);
  if (profile?.features?.themes) requiredString(profile?.brand?.logo?.dark, 'brand.logo.dark', errors);

  // Check fonts
  requiredString(profile?.brand?.fonts?.heading, 'brand.fonts.heading', errors);
  requiredString(profile?.brand?.fonts?.body, 'brand.fonts.body', errors);

  // Check color palette and custom loader - accent and progress are optional
  let themes = ['light'];
  if (profile?.features?.themes) themes.push('dark');
  themes.forEach((mode) => {
    ['primary', 'secondary', 'background', 'paper', 'text'].forEach((token) => {
      requiredString(
        profile?.brand?.palette?.[mode]?.[token],
        `brand.palette.${mode}.${token}`,
        errors
      );
    });

    if (profile?.brand?.customLoader?.enabled) {
      requiredString(
        profile?.brand?.customLoader?.loader?.[mode],
        `brand.customLoader.loader.${mode}`,
        errors
      );
    }
  });

  // Check image bucket base URL
  requiredString(profile?.content?.imageBucketBaseUrl, 'content.imageBucketBaseUrl', errors);

  // Check search provider and base URLs - if not "elastic", provide the external base URLs of the search service
  if (!['elastic', 'enterprise'].includes(profile?.search?.provider)) {
    errors.push('search.provider must be either "elastic" or "enterprise"');
  }
  if (profile?.search?.provider === 'enterprise') {
    requiredString(profile?.search?.frontendBaseUrl, 'search.frontendBaseUrl', errors);
    requiredString(profile?.search?.apiBaseUrl, 'search.apiBaseUrl', errors);
  }

  // Check environments and header bar colors as well as API base URLs
  ['local', 'development', 'production'].forEach((environmentName) => {
    if (!profile?.environments?.[environmentName]) {
      errors.push(`environments.${environmentName} is missing`);
      return;
    }
    requiredString(
      profile.environments[environmentName].apiBaseUrl,
      `environments.${environmentName}.apiBaseUrl`,
      errors
    );
    themes.forEach((mode) => {
      requiredString(
        profile.environments[environmentName]?.headerBar?.[mode]?.backgroundColor,
        `environments.${environmentName}.headerBar.${mode}.backgroundColor`,
        errors
      );
      requiredString(
        profile.environments[environmentName]?.headerBar?.[mode]?.color,
        `environments.${environmentName}.headerBar.${mode}.color`,
        errors
      );
    });
  });

  // Check authorization
  if (profile?.auth?.enabled) {
    requiredString(profile.auth.authority, 'auth.authority', errors);
    requiredString(profile.auth.clientId, 'auth.clientId', errors);
    requiredString(profile.auth.scope, 'auth.scope', errors);
  } else if (
    profile?.features?.account ||
    profile?.features?.admin ||
    profile?.features?.collections
  ) {
    errors.push('account, admin, and collections features require auth.enabled');
  }

  return errors;
};

const getOrigin = () =>
  typeof window !== 'undefined' && window.location?.origin
    ? trimTrailingSlash(window.location.origin)
    : 'http://localhost';

// Creates a configuration error result
const createInvalidConfig = ({ oem, environment, message, details = [] }) =>
  deepFreeze({
    valid: false,
    error: {
      title: 'OEM configuration error',
      message,
      details,
      oem,
      environment,
    },

    // ID of the selecte OEM profile
    oem: oem || 'unknown',
    displayName: 'Online Help',

    // Selected environment (e.g., local, development, production)
    environment: environment || 'unknown',
    defaults: runtimeConfig.defaults.defaults,

    // Documentation options
    options: {
      products: runtimeConfig.defaults.products,
      languages: runtimeConfig.defaults.languages,
      preferredLanguage: '',
    },

    // API and frontend URLs
    urls: {
      apiBase: '',
      frontendBase: getOrigin(),
    },

    // Search provider (If not "elastic", provide the external base URLs of the search service)
    search: { provider: 'elastic' },

    // Image bucket base URL (for images)
    content: { imageBucketBaseUrl: '' },

    // Metadata
    metadata: {
      title: '',
      description: '',
      manifest: '',
    },

    // Colors - Copa-Data defaults
    fonts: { heading: 'sans-serif', body: 'sans-serif', code: 'monospace', stylesheets: [] },
    palette: {
      light: {
        primary: "#202d4f",
        secondary: "#667787",
        background: "#ffffff",
        paper: "#ffffff",
        text: "#1b1b1f"
      },
      dark: {
        primary: "#202d4f",
        secondary: "#25282d",
        background: "#1b1b1f",
        paper: "#25282d",
        text: "#ffffff"
      },
      accent: "#8ab4f8"
    },

    // Header Bar colors - Copa-Data defaults
    headerBar: {
      light: {
        backgroundColor: "#e6f7ff",
        color: "#000000",
        borderBottom: "none",
        boxShadow: "0 2px 8px rgba(0,0,0,0.18)"
      },
      dark: {
        backgroundColor: "#000000",
        color: "#ffffff",
        borderBottom: "1px solid #25282d",
        boxShadow: "0 2px 8px rgba(0,0,0,0.45)"
      }
    },

    // Website Tab Icons
    icon: {
      favicon: '',
      appleTouchIcon: '',
    },

    // Logo SVG shown on the Header Bar
    logo: {
      light: '',
      dark: '',
      viewBox: '',
      padding: '',
      alt: '',
      href: '',
    },

    customLoader: { enabled: false },

    links: {
      supportEmail: '',
      engineeringAssistant: '',
    },
    auth: { enabled: false },
    analytics: { enabled: false },
    features: {
      themes: true,
      account: false,
      admin: false,
      collections: false,
      apiDocumentation: false,
      llm: false,
      pdf: false
    },
  });

const resolveAppConfig = ({
  oem = getClientEnv('APP_OEM') || runtimeConfig.defaults.tenant,
  environment = null,
} = {}) => {
  const tenantKey = String(oem || '').trim().toLowerCase();
  const requestedEnvironment = environment || getClientEnv('APP_ENV');
  const resolvedEnvironment = requestedEnvironment
    ? normalizeEnvironment(requestedEnvironment)
    : normalizeEnvironment(runtimeConfig.defaults.defaults.environment);

  if (!oemProfiles[tenantKey]) {
    return createInvalidConfig({
      oem: tenantKey,
      environment: resolvedEnvironment,
      message: `No OEM profile exists for "${tenantKey || '(empty)'}".`,
      details: [`Available OEMs: ${Object.keys(oemProfiles).join(', ')}`],
    });
  }

  if (!resolvedEnvironment) {
    return createInvalidConfig({
      oem: tenantKey,
      environment: String(requestedEnvironment),
      message: `APP_ENV "${requestedEnvironment}" is not supported.`,
      details: ['Use LOCAL, DEV, or PROD.'],
    });
  }

  const tenantConfig = oemProfiles[tenantKey];
  const validationErrors = validateProfile(tenantConfig);
  if (tenantConfig.id !== tenantKey) {
    validationErrors.push(`profile id "${tenantConfig.id}" does not match registry key "${tenantKey}"`);
  }
  if (validationErrors.length) {
    return createInvalidConfig({
      oem: tenantKey,
      environment: resolvedEnvironment,
      message: `The "${tenantKey}" OEM profile is incomplete.`,
      details: validationErrors,
    });
  }

  const environmentConfig = tenantConfig.environments[resolvedEnvironment];

  const frontendBaseUrl = getOrigin();
  const apiBaseUrl = trimTrailingSlash(environmentConfig.apiBaseUrl);
  const titleSuffix = environmentConfig.titleSuffix || '';
  const title = `${tenantConfig.displayName}${titleSuffix}`;

  return deepFreeze({
    valid: true,
    error: null,

    // ID of the selecte OEM profile
    oem: tenantKey,
    displayName: tenantConfig.displayName,

    // Selected environment (e.g., local, development, production)
    environment: resolvedEnvironment,

    defaults: runtimeConfig.defaults.defaults,

    // Documentation options
    options: {
      products: runtimeConfig.defaults.products,
      languages: runtimeConfig.defaults.languages,
      preferredLanguage: tenantConfig.preferredLanguage, // Default language for documentation
    },

    // API and frontend URLs
    urls: {
      apiBase: apiBaseUrl,
      frontendBase: frontendBaseUrl,
    },

    // Search provider (If not "elastic", provide the external base URLs of the search service)
    search: {
      provider: tenantConfig.search?.provider || "elastic",
      frontendBaseUrl: trimTrailingSlash(tenantConfig.search?.frontendBaseUrl),
      apiBaseUrl: trimTrailingSlash(tenantConfig.search?.apiBaseUrl),
    },

    // Image bucket base URL (for images)
    content: {
      imageBucketBaseUrl: trimTrailingSlash(tenantConfig.content.imageBucketBaseUrl),
    },

    // Metadata
    metadata: {
      title,
      description: tenantConfig.description || tenantConfig.displayName,
      manifest: tenantConfig.brand.icon.manifest || `/brands/${tenantKey}/manifest.json`,
    },

    // Fonts
    fonts : {
      heading: tenantConfig.brand.fonts.heading,
      body: tenantConfig.brand.fonts.body,
      code: tenantConfig.brand.fonts.code,
      stylesheets: tenantConfig.brand.fonts.stylesheets,
    },

    // Colors
    palette: {
      light: tenantConfig.brand.palette.light,
      dark: tenantConfig.brand.palette.dark || null,
      progress: tenantConfig.brand.palette.progress || null,
      accent: tenantConfig.brand.palette.accent || tenantConfig.brand.palette.dark?.primary || null,
    },

    // Header Bar colors
    headerBar: {
      light: environmentConfig.headerBar.light,
      dark: environmentConfig.headerBar.dark || null,
    },

    // Website Tab Icons
    icon: tenantConfig.brand.icon,

    // Logo SVG shown on the Header Bar
    logo: {
      light: tenantConfig.brand.logo.light, // Logo for light mode
      dark: tenantConfig.brand.logo.dark, // Logo for dark mode
      viewBox: tenantConfig.brand.logo.viewBox || "0 0 100 100", // ViewBox for the logo SVG
      padding: tenantConfig.brand.logo.padding || "0px",
      alt: tenantConfig.brand.logo.alt,
      href: tenantConfig.brand.logo.href,
    },

    // Custom Loader - If not defined, the default loader is used
    customLoader: {
      enabled: tenantConfig.brand.customLoader?.enabled || false,
      loader: tenantConfig.brand.customLoader?.loader || null,
    },

    links: tenantConfig.links,
    auth: tenantConfig.auth || { enabled: false },
    analytics: tenantConfig.analytics || { enabled: false },
    features: tenantConfig.features,
  });
};

// General configuration
export const APP_CONFIG = resolveAppConfig();
export const OEM = APP_CONFIG.oem;
export const ENV = APP_CONFIG.environment;
export const DISPLAY_NAME = APP_CONFIG.displayName;
export const DEFAULTS = APP_CONFIG.defaults;
export const CONFIG_ERROR = APP_CONFIG.error;
export const CONFIG_VALID = APP_CONFIG.valid;
export const METADATA = APP_CONFIG.metadata;

// UI
export const LOGO = APP_CONFIG.logo;
export const ICON = APP_CONFIG.icon;
export const FONTS = APP_CONFIG.fonts;
export const PALETTE = APP_CONFIG.palette;
export const HEADER_BAR = APP_CONFIG.headerBar;
export const CUSTOM_LOADER = APP_CONFIG.customLoader;

// Features
export const FEATURES = APP_CONFIG.features;

// Additional configuration
export const AUTH = APP_CONFIG.auth;
export const SEARCH = APP_CONFIG.search;
export const ANALYTICS = APP_CONFIG.analytics;

// URLs
export const URLS = APP_CONFIG.urls;
export const LINKS = APP_CONFIG.links;
export const CONTENT = APP_CONFIG.content;

// Documentation options
export const OPTIONS = APP_CONFIG.options;

export const buildFolderPath = ({product, version, language}) =>
    `${product}_${OEM}_${version}_${language}`;

export const buildImageBaseUrl = ({product, version, language}) =>
  `${CONTENT.imageBucketBaseUrl}/${buildFolderPath({product, version, language})}`;
